import React, { useState, useMemo } from 'react';
import { Activity } from 'lucide-react';
import { useUnifiedPrices } from '../contexts/UnifiedPriceContext';
import { getListedCfdInstrument } from '../../supabase/functions/_shared/cfdInstruments';
import RecentTrades from './RecentTrades';
import { TradingMode } from '../App';
import { CFD_INSTRUMENTS } from '../constants/tradingPairs';
import { useSimulatedOrderBook } from '../hooks/useSimulatedOrderBook';

interface OrderBookWithTradesProps {
  selectedPair: string;
  tradingMode: TradingMode;
}

const getPrecisionForSymbol = (symbol: string): number => {
  const isJpyPair = symbol.includes('JPY');
  const instrument = CFD_INSTRUMENTS.find(i => i.symbol === symbol);
  const isForex = instrument?.type === 'forex';

  if (isJpyPair) return 3;
  if (isForex) return 5;
  if (symbol.includes('XAU') || symbol.includes('GOLD')) return 2;
  if (symbol.includes('XAG') || symbol.includes('SILVER')) return 3;
  if (symbol.includes('WTICO') || symbol.includes('BCO') || symbol.includes('OIL')) return 3;
  if (symbol.includes('NATGAS')) return 4;
  if (symbol.endsWith('USDT')) return symbol.includes('BTC') || symbol.includes('ETH') ? 2 : 4;
  return 2;
};

const OrderBookWithTrades: React.FC<OrderBookWithTradesProps> = ({
  selectedPair,
  tradingMode
}) => {
  const { getPriceBySymbol } = useUnifiedPrices();
  const [activeTab, setActiveTab] = useState<'orderBook' | 'recentTrades'>('orderBook');

  const precision = useMemo(() => getPrecisionForSymbol(selectedPair), [selectedPair]);

  const currentPrice = getPriceBySymbol(selectedPair);

  const orderBook = useSimulatedOrderBook(selectedPair, currentPrice, 12);
  const quoteAvailable = Number.isFinite(currentPrice) && currentPrice > 0;
  const quoteCurrency = getListedCfdInstrument(selectedPair)?.type === 'index' ? 'PTS' : tradingMode === 'cfd' ? 'USD' : 'USDT';

  const formatPrice = (price: number) => price.toFixed(precision);
  const formatAmount = (amount: number) => amount.toFixed(4);

  const maxBidTotal = orderBook.bids.length > 0 ? orderBook.bids[orderBook.bids.length - 1].total : 0;
  const maxAskTotal = orderBook.asks.length > 0 ? orderBook.asks[orderBook.asks.length - 1].total : 0;
  const maxTotal = Math.max(maxBidTotal, maxAskTotal);

  const totalBidVolume = orderBook.bids.reduce((sum, b) => sum + b.amount, 0);
  const totalAskVolume = orderBook.asks.reduce((sum, a) => sum + a.amount, 0);
  const totalVolume = totalBidVolume + totalAskVolume;
  const bidPercentage = totalVolume > 0 ? (totalBidVolume / totalVolume) * 100 : 50;

  return (
    <div className="h-full flex flex-col bg-white" translate="no">
      <div className="flex items-center border-b border-slate-200 px-2">
        <button
          onClick={() => setActiveTab('orderBook')}
          className={`px-4 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'orderBook'
              ? 'text-slate-900'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Order Book
          {activeTab === 'orderBook' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('recentTrades')}
          className={`px-4 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'recentTrades'
              ? 'text-slate-900'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Recent Trades
          {activeTab === 'recentTrades' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
          )}
        </button>
      </div>

      {!quoteAvailable ? (
        <div className="p-4 text-sm text-slate-500">Waiting for a market quote activity.</div>
      ) : activeTab === 'orderBook' ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="grid grid-cols-3 text-xs font-medium p-2 border-b border-slate-100 text-slate-500">
            <div>Price ({quoteCurrency})</div>
            <div className="text-right">Qty</div>
            <div className="text-right">Total</div>
          </div>

          <div className="flex-1 overflow-auto">
            <div className="flex flex-col-reverse">
              {orderBook.asks.slice().reverse().map((ask, index) => (
                <div
                  key={`ask-${index}`}
                  className="grid grid-cols-3 text-xs py-1 px-2 relative hover:bg-red-50 transition-colors"
                >
                  <div className="font-mono text-red-500 font-medium z-10">{formatPrice(ask.price)}</div>
                  <div className="text-right font-mono text-slate-600 z-10">{formatAmount(ask.amount)}</div>
                  <div className="text-right font-mono text-slate-600 z-10">{formatAmount(ask.total)}</div>
                  <div
                    className="absolute right-0 top-0 bottom-0 bg-red-100"
                    style={{ width: `${(ask.total / maxTotal) * 100}%`, opacity: 0.4 }}
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between p-2 bg-slate-50 border-y border-slate-200">
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 font-mono">
                  {currentPrice.toFixed(precision)}
                </span>
                <Activity size={14} className="text-slate-400" />
              </div>
              <span className="text-xs text-slate-500">Reference price</span>
            </div>

            <div>
              {orderBook.bids.map((bid, index) => (
                <div
                  key={`bid-${index}`}
                  className="grid grid-cols-3 text-xs py-1 px-2 relative hover:bg-green-50 transition-colors"
                >
                  <div className="font-mono text-green-500 font-medium z-10">{formatPrice(bid.price)}</div>
                  <div className="text-right font-mono text-slate-600 z-10">{formatAmount(bid.amount)}</div>
                  <div className="text-right font-mono text-slate-600 z-10">{formatAmount(bid.total)}</div>
                  <div
                    className="absolute right-0 top-0 bottom-0 bg-green-100"
                    style={{ width: `${(bid.total / maxTotal) * 100}%`, opacity: 0.4 }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="p-2 border-t border-slate-200">
            <div className="flex items-center gap-1 text-xs mb-2">
              <span className="text-slate-500">B</span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-200">
                <div
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${bidPercentage}%` }}
                />
              </div>
              <span className="text-slate-500">S</span>
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>{bidPercentage.toFixed(1)}%</span>
              <span>{(100 - bidPercentage).toFixed(1)}%</span>
            </div>
          </div>
        </div>
      ) : (
        <RecentTrades selectedPair={selectedPair} quoteCurrency={quoteCurrency} />
      )}
    </div>
  );
};

export default OrderBookWithTrades;
